import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { ASPECT_HEIGHT_LISTING, ASPECT_WIDTH_LISTING } from '../../config/configLayout';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { fetchStripeAccount } from '../../ducks/stripeConnectAccount.duck';
import { getPractitionerChecklistState } from '../../util/practitionerChecklist';
import {
  getUserTypeFromCurrentUser,
  isPractitioner,
} from '../../util/soulwise-marketplace/userType';
import {
  completePractitionerOnboardingTransaction,
  isPractitionerOnboardingProcess,
  startPractitionerOnboardingTransaction,
  syncPractitionerOnboardingChecklist,
} from '../../util/practitionerOnboardingTransaction';
import {
  isCampaignInboxListing,
  isPractitionerCampaignProcess,
} from '../../util/practitionerCampaign';

// ================ Action types ================ //

export const FETCH_OWN_LISTINGS_REQUEST =
  'app/PractitionerDashboardPage/FETCH_OWN_LISTINGS_REQUEST';
export const FETCH_OWN_LISTINGS_SUCCESS =
  'app/PractitionerDashboardPage/FETCH_OWN_LISTINGS_SUCCESS';
export const FETCH_OWN_LISTINGS_ERROR = 'app/PractitionerDashboardPage/FETCH_OWN_LISTINGS_ERROR';

export const FETCH_SALES_REQUEST = 'app/PractitionerDashboardPage/FETCH_SALES_REQUEST';
export const FETCH_SALES_SUCCESS = 'app/PractitionerDashboardPage/FETCH_SALES_SUCCESS';
export const FETCH_SALES_ERROR = 'app/PractitionerDashboardPage/FETCH_SALES_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchListingsInProgress: false,
  fetchListingsError: null,
  ownListings: [],
  fetchSalesInProgress: false,
  fetchSalesError: null,
  saleRefs: [],
};

export default function practitionerDashboardPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_OWN_LISTINGS_REQUEST:
      return { ...state, fetchListingsInProgress: true, fetchListingsError: null };
    case FETCH_OWN_LISTINGS_SUCCESS:
      return {
        ...state,
        fetchListingsInProgress: false,
        ownListings: payload.ownListings,
      };
    case FETCH_OWN_LISTINGS_ERROR:
      return { ...state, fetchListingsInProgress: false, fetchListingsError: payload };

    case FETCH_SALES_REQUEST:
      return { ...state, fetchSalesInProgress: true, fetchSalesError: null };
    case FETCH_SALES_SUCCESS:
      return {
        ...state,
        fetchSalesInProgress: false,
        saleRefs: entityRefs(payload.data.data),
      };
    case FETCH_SALES_ERROR:
      return { ...state, fetchSalesInProgress: false, fetchSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOwnListingsRequest = () => ({ type: FETCH_OWN_LISTINGS_REQUEST });
const fetchOwnListingsSuccess = ownListings => ({
  type: FETCH_OWN_LISTINGS_SUCCESS,
  payload: { ownListings },
});
const fetchOwnListingsError = e => ({
  type: FETCH_OWN_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchSalesRequest = () => ({ type: FETCH_SALES_REQUEST });
const fetchSalesSuccess = response => ({
  type: FETCH_SALES_SUCCESS,
  payload: response,
});
const fetchSalesError = e => ({
  type: FETCH_SALES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const RESULT_PAGE_SIZE = 100;

export const fetchOwnListings = config => (dispatch, getState, sdk) => {
  dispatch(fetchOwnListingsRequest());

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.ownListings
    .query({
      page: 1,
      perPage: RESULT_PAGE_SIZE,
      include: ['images'],
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    })
    .then(response => {
      const ownListings = denormalisedResponseEntities(response).filter(
        listing => !isCampaignInboxListing(listing)
      );
      dispatch(fetchOwnListingsSuccess(ownListings));
      return response;
    })
    .catch(e => {
      dispatch(fetchOwnListingsError(storableError(e)));
      throw e;
    });
};

export const fetchSales = () => (dispatch, getState, sdk) => {
  dispatch(fetchSalesRequest());

  // Prefer listing-card crop matching homepage ListingCard (157:74)
  const variantPrefix = 'listing-card';
  const aspectRatio = ASPECT_HEIGHT_LISTING / ASPECT_WIDTH_LISTING;

  return sdk.transactions
    .query({
      only: 'sale',
      lastTransitions: getAllTransitionsForEveryProcess(),
      page: 1,
      perPage: RESULT_PAGE_SIZE,
      include: [
        'listing',
        'listing.images',
        'provider',
        'customer',
        'customer.profileImage',
        'booking',
      ],
      'fields.transaction': [
        'processName',
        'lastTransition',
        'lastTransitionedAt',
        'transitions',
        'payinTotal',
        'payoutTotal',
        'lineItems',
      ],
      'fields.listing': [
        'title',
        'price',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'state',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    })
    .then(response => {
      const data = (response?.data?.data || []).filter(
        tx =>
          !isPractitionerOnboardingProcess(tx?.attributes?.processName) &&
          !isPractitionerCampaignProcess(tx?.attributes?.processName)
      );
      const filtered = {
        ...response,
        data: {
          ...response.data,
          data,
        },
      };
      dispatch(addMarketplaceEntities(filtered));
      dispatch(fetchSalesSuccess(filtered));
      return filtered;
    })
    .catch(e => {
      dispatch(fetchSalesError(storableError(e)));
      throw e;
    });
};

export const completePractitionerOnboardingEmail = checklist => (dispatch, getState, sdk) => {
  const currentUser = getState().user?.currentUser;
  return completePractitionerOnboardingTransaction(sdk, currentUser, checklist || null).then(() =>
    dispatch(fetchCurrentUser())
  );
};

export const syncPractitionerOnboardingChecklistEmail = checklist => (dispatch, getState, sdk) => {
  const currentUser = getState().user?.currentUser;
  return syncPractitionerOnboardingChecklist(sdk, currentUser, checklist);
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // Mark listings/sales loading immediately so UI doesn't flash empty "0 listings".
  dispatch(fetchOwnListingsRequest());
  dispatch(fetchSalesRequest());

  return dispatch(fetchCurrentUser()).then(currentUser => {
    const stripeConnected =
      !!currentUser?.attributes?.stripeConnected || !!currentUser?.stripeAccount?.id;

    const salesPromise = dispatch(fetchSales()).catch(() => null);
    const listingsPromise = dispatch(fetchOwnListings(config)).catch(() => null);
    const stripePromise = stripeConnected
      ? dispatch(fetchStripeAccount()).catch(() => null)
      : Promise.resolve(null);

    // Load listings + Stripe first so backfill welcome protectedData mirrors the
    // real Getting Started checklist (not just emailVerified from currentUser alone).
    return Promise.all([salesPromise, listingsPromise, stripePromise]).then(() => {
      const state = getState();
      const user = state.user?.currentUser || currentUser;
      const ownListings = state.PractitionerDashboardPage?.ownListings || [];
      const stripeAccount = state.stripeConnectAccount?.stripeAccount || null;
      const checklist = getPractitionerChecklistState({
        currentUser: user,
        ownListings,
        stripeAccount,
      });

      const isPractitionerUser = user && isPractitioner(getUserTypeFromCurrentUser(user));

      // Backfill / primary send: verified + incomplete checklist + no healthy TX.
      // Checklist flags are written into protectedData at start-onboarding.
      const onboardingPromise = isPractitionerUser
        ? startPractitionerOnboardingTransaction(sdk, user, {
            checklist,
            ownListings,
            stripeAccount,
          })
            .then(() => dispatch(fetchCurrentUser()).catch(() => null))
            .catch(() => null)
        : Promise.resolve(null);

      return onboardingPromise.then(() => {
        // Cancel P3D reminder even if the checklist UI never mounts (user finished
        // steps on other routes and only hits dashboard load / soft refresh).
        // Otherwise sync step flags so the delayed reminder email reflects progress.
        const refreshedUser = getState().user?.currentUser || user;
        const refreshedChecklist = getPractitionerChecklistState({
          currentUser: refreshedUser,
          ownListings,
          stripeAccount,
        });
        if (refreshedChecklist.isComplete) {
          return dispatch(completePractitionerOnboardingEmail(refreshedChecklist)).catch(
            () => null
          );
        }
        return dispatch(syncPractitionerOnboardingChecklistEmail(refreshedChecklist)).catch(
          () => null
        );
      });
    });
  });
};
